// CardScience Library — educational SEO articles in brand tone.
// Body is an array of blocks: {type: 'p'|'h2'|'impact'|'list'|'disclaimer', text|items}.

export const ARTICLES = {
  "should-you-clean-a-card-before-grading": {
    slug: "should-you-clean-a-card-before-grading",
    path: "/library/should-you-clean-a-card-before-grading",
    title: "Should You Clean a Card Before Grading?",
    metaDescription:
      "A straight answer to one of the hobby's most-asked questions: should you clean a card before submitting it for grading? What responsible cleaning can and cannot do.",
    excerpt:
      "The honest answer isn't yes or no — it's 'it depends on the card.' Here's how to think about cleaning before grading without doing more harm than good.",
    datePublished: "2026-01-15",
    readTime: "6 min read",
    category: "Grading Prep",
    body: [
      { type: "p", text: "It's the question we hear more than any other: should I clean my card before I send it to be graded? The honest answer is that it depends entirely on the card in front of you — and the goal is never to force a result that isn't there." },
      { type: "h2", text: "What cleaning can responsibly do" },
      { type: "p", text: "Responsible cleaning is about removing surface contamination that isn't part of the card — fingerprints, residue, loose debris, certain kinds of surface haze on chrome finishes. When a card carries removable material on its surface, addressing it can help the card present honestly." },
      { type: "impact", text: "Cleaning removes what shouldn't be there. It cannot remove what is." },
      { type: "h2", text: "What cleaning cannot do" },
      { type: "p", text: "Cleaning cannot fix scratches, dents, creases, print lines, centering, edge wear, corner wear or structural whitening. These are part of the card. No responsible process removes them, and any process that claims to is altering the card — which is a different thing entirely, and not something we do." },
      { type: "list", items: [
        "Scratches and surface gouges — permanent",
        "Creases and bends — permanent",
        "Print defects and print lines — factory-original",
        "Centering — cannot be changed responsibly",
        "Structural corner and edge wear — permanent",
      ]},
      { type: "h2", text: "So should you clean before grading?" },
      { type: "p", text: "Sometimes yes, sometimes no. A card with removable surface residue may present better after responsible cleaning. A card whose issues are all structural won't benefit — and a fragile vintage card may be better left alone. The right answer comes from evaluating the specific card, not from a rule of thumb." },
      { type: "p", text: "That's why every card we handle is evaluated individually before any work begins. If cleaning won't help your card, we'll tell you." },
      { type: "disclaimer", text: "Independent grading companies determine all grades under their own standards. No grade is guaranteed, and cleaning does not guarantee a grade or acceptance by any grading company." },
    ],
    related: [
      { label: "Card Grading Prep", to: "/card-grading-prep" },
      { label: "How It Works", to: "/how-it-works" },
    ],
  },

  "gentle-by-design-what-it-means": {
    slug: "gentle-by-design-what-it-means",
    path: "/library/gentle-by-design-what-it-means",
    title: "Gentle By Design: Why Process Beats Product",
    metaDescription:
      "There's no magic bottle. Here's why CardScience HQ's 'gentle by design' philosophy — no harsh chemicals, no direct heat, no shortcuts — protects your collectibles.",
    excerpt:
      "Collectors always ask what we use. The more important question is what we refuse to do. Here's the philosophy behind gentle-by-design.",
    datePublished: "2026-01-22",
    readTime: "5 min read",
    category: "Our Process",
    body: [
      { type: "p", text: "People assume the secret to card care is a product — some special solution that makes cards look new. It isn't. The secret is the process, and more specifically, the discipline of what we refuse to do." },
      { type: "h2", text: "The secret isn't a bottle" },
      { type: "p", text: "Gentle-by-design means we start from the assumption that a collectible is irreplaceable, and we never use a method that risks it. Four principles are non-negotiable:" },
      { type: "list", items: [
        "No harsh chemicals",
        "No direct heat",
        "No aggressive shortcuts",
        "No one-size-fits-all process",
      ]},
      { type: "impact", text: "The fastest way to ruin a card is to treat it like every other card." },
      { type: "h2", text: "Why one-size-fits-all fails" },
      { type: "p", text: "A vintage newsprint comic, a modern chrome refractor and a WOTC holo respond to handling in completely different ways. A process appropriate for one can permanently damage another. Individual evaluation isn't a marketing line — it's the only responsible way to work." },
      { type: "h2", text: "Cleaner does not mean perfect" },
      { type: "p", text: "Even a professionally cared-for card can still carry defects that no process removes. We would rather tell you the truth about what's possible than promise a result we can't responsibly deliver. In fact, we turn away a meaningful share of the collectibles submitted to us." },
      { type: "disclaimer", text: "CardScience HQ is not affiliated with any grading company. No grade is guaranteed and past results do not guarantee future results." },
    ],
    related: [
      { label: "Our Process", to: "/our-process" },
      { label: "Trust Center", to: "/trust-center" },
    ],
  },

  "protecting-cards-in-the-mail": {
    slug: "protecting-cards-in-the-mail",
    path: "/library/protecting-cards-in-the-mail",
    title: "How to Protect Cards When You Mail Them",
    metaDescription:
      "A practical guide to safely mailing valuable trading cards and comics — packaging, documentation, insurance and the one rule that prevents most problems.",
    excerpt:
      "Most shipping disasters are preventable. Here's how to package, document and insure a collectible so it arrives exactly as it left.",
    datePublished: "2026-02-03",
    readTime: "5 min read",
    category: "Shipping",
    body: [
      { type: "p", text: "Sending a collectible through the mail feels risky because it can be — but nearly every problem we've seen is preventable with the right packaging, documentation and a little discipline." },
      { type: "h2", text: "Package for the worst day the parcel will have" },
      { type: "p", text: "Cards should be secured against movement, moisture and bending. A rigid outer layer, protection against water, and enough internal support that nothing shifts in transit. Comics need rigid, full-coverage support that respects the spine." },
      { type: "h2", text: "Document before it leaves your hands" },
      { type: "p", text: "Photograph the collectible and its condition before packing. For declared values of $50 or more, keep documentation of what you sent. For $500 or more, insured shipping is strongly advised." },
      { type: "impact", text: "The one rule: never ship before your submission is approved." },
      { type: "h2", text: "Wait for approval" },
      { type: "p", text: "Our process reviews every submission before anything is mailed. This isn't bureaucracy — it ensures your item is expected, logged and handled with a documented chain of custody the moment it arrives." },
      { type: "disclaimer", text: "Insurance and shipping decisions ultimately rest with you and your carrier. See our Shipping & Insurance policy for full details." },
    ],
    related: [
      { label: "Mail-In Card Cleaning", to: "/mail-in-card-cleaning" },
      { label: "Shipping & Insurance", to: "/shipping-insurance" },
    ],
  },

  "how-to-store-your-cards": {
    slug: "how-to-store-your-cards",
    path: "/library/how-to-store-your-cards",
    title: "How to Store Your Trading Cards",
    metaDescription:
      "A practical guide to storing trading cards — sleeves, rigid holders, orientation, temperature, humidity, light and long-term protection from the CardScience HQ Card Care Lab.",
    excerpt:
      "The right storage does more for a collection than almost anything else. Sleeves, holders, orientation, climate and light — here's how to protect cards for the long term.",
    datePublished: "2026-03-04",
    readTime: "6 min read",
    category: "Card Care Lab",
    hub: "card-care-lab",
    body: [
      { type: "p", text: "Storage is the quiet part of collecting that decides how a card looks in five, ten or twenty years. Most damage we see in the lab didn't happen in a dramatic moment — it happened slowly, in a drawer, a shoebox or a binder that wasn't right for the card." },
      { type: "h2", text: "Start with the right layers" },
      { type: "p", text: "Think in layers of protection. A soft penny sleeve goes on first, guarding the surface from dust and friction. That sleeved card then goes into a rigid or semi-rigid holder — a top-loader or a one-touch magnetic case for more valuable cards — so nothing bends or flexes." },
      { type: "list", items: [
        "Penny sleeve first — soft protection against dust and micro-scratches",
        "Top-loader or semi-rigid holder for everyday protection",
        "One-touch magnetic holders for higher-value cards",
        "Team bags or resealable sleeves over the holder to seal out moisture",
      ]},
      { type: "h2", text: "Orientation matters" },
      { type: "p", text: "Store cards upright, standing on their long edge, the way books sit on a shelf — not stacked flat under weight for long periods. Stacking puts steady pressure on the cards at the bottom and can cause indentations over time." },
      { type: "h2", text: "Control the environment" },
      { type: "p", text: "Cards are happiest in stable, moderate conditions: cool, dry and out of direct light. Aim for a consistent room temperature and moderate humidity. Wide swings in heat and moisture are the enemy — they encourage warping, and in comics and paper stock, foxing and curl." },
      { type: "impact", text: "Cool, dry, dark and stable beats cold, hot or damp every time." },
      { type: "h2", text: "Keep light off your cards" },
      { type: "p", text: "UV and even strong indoor light fade inks and dull finishes over years of exposure. Store cards away from windows, and if you display them, use UV-filtering cases and keep them out of direct sunlight." },
      { type: "disclaimer", text: "Good storage protects condition; it does not repair existing damage or guarantee any grading outcome. Every card is different." },
    ],
    related: [
      { label: "Why Cards Get Damaged", to: "/library/why-cards-get-damaged" },
      { label: "The Card Care Lab", to: "/card-care-lab" },
    ],
  },

  "what-not-to-do-with-your-cards": {
    slug: "what-not-to-do-with-your-cards",
    path: "/library/what-not-to-do-with-your-cards",
    title: "What NOT to Do With Your Cards",
    metaDescription:
      "The most common mistakes that permanently damage trading card surfaces, finishes, edges and corners — and how to avoid them. From the CardScience HQ Card Care Lab.",
    excerpt:
      "Most card damage is self-inflicted and avoidable. Here are the handling and cleaning mistakes that cause permanent harm — and what to do instead.",
    datePublished: "2026-03-05",
    readTime: "5 min read",
    category: "Card Care Lab",
    hub: "card-care-lab",
    body: [
      { type: "p", text: "We could fill a book with the ways a good card gets ruined by good intentions. Almost all of it is preventable. If you take nothing else from the Card Care Lab, take this list of things to never do." },
      { type: "h2", text: "Never try to \u2018fix\u2019 a card with household products" },
      { type: "p", text: "Water, spit, erasers, toothpaste, glass cleaner, magic sponges and heat guns have all destroyed cards that walked in looking fine. Household chemicals and abrasives strip finishes, cloud surfaces and leave permanent marks. There is no kitchen-cupboard shortcut to a cleaner card." },
      { type: "list", items: [
        "Don't use water, saliva or household cleaners on a card",
        "Don't rub surfaces with erasers, sponges or paper towels",
        "Don't apply heat from a hair dryer, iron or heat gun",
        "Don't tape a card or its holder — adhesive damage is permanent",
        "Don't write on, trim or bend a card for any reason",
      ]},
      { type: "h2", text: "Handle less, and handle clean" },
      { type: "p", text: "Every touch transfers oils. Hold cards by the edges with clean, dry hands, and keep food, drinks and lotions far away from your collection. Fingerprints on a glossy surface are one of the most common issues we see." },
      { type: "impact", text: "The surface you can't un-scratch is the one you should never test on." },
      { type: "h2", text: "Don't store loose or stacked under weight" },
      { type: "p", text: "Loose cards rattling in a box get edge and corner wear. Cards stacked flat under heavy stacks get pressure dents. Sleeve, protect and store upright." },
      { type: "p", text: "If a card genuinely needs cleaning or attention, that's exactly what a professional evaluation is for — so a small issue doesn't become a permanent one." },
      { type: "disclaimer", text: "Some damage cannot be undone by anyone. Responsible care can improve presentation but cannot erase permanent defects, and no grade is ever guaranteed." },
    ],
    related: [
      { label: "How to Store Your Cards", to: "/library/how-to-store-your-cards" },
      { label: "The Card Care Lab", to: "/card-care-lab" },
    ],
  },

  "card-care-products-we-trust": {
    slug: "card-care-products-we-trust",
    path: "/library/card-care-products-we-trust",
    title: "Card-Care Products We Use & Trust",
    metaDescription:
      "The consumer storage and protection products we recommend for collectors — sleeves, holders, cases and climate control. Selected for specific purposes by the CardScience HQ Card Care Lab.",
    excerpt:
      "Not gear reviews — a practical, purpose-led look at the storage and protection products worth trusting, and a clear line between consumer protection and our professional processes.",
    datePublished: "2026-03-06",
    readTime: "5 min read",
    category: "Card Care Lab",
    hub: "card-care-lab",
    body: [
      { type: "p", text: "Collectors ask us constantly what we trust. When it comes to storage and protection, we're happy to share — because the right consumer products prevent the exact damage we spend our days working around." },
      { type: "h2", text: "Protection products worth trusting" },
      { type: "p", text: "We recommend products by the job they do, not the brand on the box. Look for archival-safe, acid-free materials and holders sized correctly for your card." },
      { type: "list", items: [
        "Soft penny sleeves in the correct size for the card",
        "Semi-rigid holders for submissions and everyday storage",
        "Top-loaders for general protection and transport",
        "One-touch magnetic cases for higher-value cards",
        "Acid-free, archival-safe boxes and dividers",
        "Silica gel packs and a hygrometer to manage humidity",
      ]},
      { type: "impact", text: "Choose materials that are archival-safe, acid-free and sized to the card." },
      { type: "h2", text: "Where consumer products end and our lab begins" },
      { type: "p", text: "This is important, and we want to be clear about it. The products above are for you: they protect and store your cards at home. They are not the same thing as the professional processes we perform in the lab." },
      { type: "p", text: "CardScience HQ's cleaning and preparation processes, the specific methods, formulations and procedures we use, are proprietary and are not published. That isn't marketing mystique; it's how we protect the integrity of the work and the cards entrusted to us. Consumer storage protects a card. Professional processes are a separate discipline, performed under controlled conditions on individually evaluated cards." },
      { type: "h2", text: "When in doubt, protect \u2014 don't experiment" },
      { type: "p", text: "If you're unsure whether a card needs more than good storage, send photos for an evaluation rather than trying a technique you read about online. Protection is a job for you; treatment is a job for the lab." },
      { type: "disclaimer", text: "Product suggestions are general guidance, not endorsements, and are not tailored to a specific card. We do not disclose proprietary cleaning techniques, formulations or procedures. No grade or outcome is guaranteed." },
    ],
    related: [
      { label: "How to Store Your Cards", to: "/library/how-to-store-your-cards" },
      { label: "The Card Care Lab", to: "/card-care-lab" },
    ],
  },

  "why-cards-get-damaged": {
    slug: "why-cards-get-damaged",
    path: "/library/why-cards-get-damaged",
    title: "Why Cards Get Damaged",
    metaDescription:
      "Fingerprints, oils, dust, moisture, humidity, UV light, friction and pressure — the real reasons trading cards degrade over time, explained by the CardScience HQ Card Care Lab.",
    excerpt:
      "Understanding how cards degrade is the first step to preventing it. Here are the everyday forces that quietly damage a collection — and how each one does its work.",
    datePublished: "2026-03-07",
    readTime: "6 min read",
    category: "Card Care Lab",
    hub: "card-care-lab",
    body: [
      { type: "p", text: "Cards rarely fail all at once. They degrade through small, repeated exposures to a handful of everyday forces. Once you can name them, you can design your storage and handling to defend against each one." },
      { type: "h2", text: "Oils and fingerprints" },
      { type: "p", text: "Skin constantly produces oils. Every bare-handed touch leaves a residue that attracts dust and, over time, etches faint marks into glossy finishes. This is the single most common surface issue we see." },
      { type: "h2", text: "Dust, dirt and friction" },
      { type: "p", text: "Dust is mildly abrasive. When a card slides against a dusty surface or an unsleeved neighbor, that grit acts like fine sandpaper, creating micro-scratches and haze on the finish." },
      { type: "h2", text: "Moisture and humidity" },
      { type: "p", text: "Water and high humidity are especially destructive. They cause warping, cockling, sticking and, in paper-heavy stock, foxing and staining. Rapid swings between humid and dry are worse than a stable, moderate level." },
      { type: "impact", text: "Most damage is cumulative \u2014 tiny exposures adding up over years." },
      { type: "h2", text: "Light and UV" },
      { type: "p", text: "Ultraviolet and strong visible light fade inks and dull finishes. A card displayed in sunlight can visibly change in months; even ambient room light adds up over years." },
      { type: "h2", text: "Pressure and mishandling" },
      { type: "p", text: "Weight on a stack causes indentations. A dropped or flexed card can crease. Loose storage lets corners and edges knock and wear. None of these are reversible in the way collectors hope." },
      { type: "list", items: [
        "Oils \u2014 from skin contact, etch glossy surfaces",
        "Dust & friction \u2014 abrasive micro-scratches and haze",
        "Moisture & humidity \u2014 warping, sticking, foxing",
        "UV & light \u2014 fading and dulling of inks and finishes",
        "Pressure & impact \u2014 dents, creases, corner and edge wear",
      ]},
      { type: "disclaimer", text: "Understanding damage helps prevent it. Existing structural damage \u2014 creases, dents, wear \u2014 is permanent, and no process or grade is guaranteed." },
    ],
    related: [
      { label: "What NOT to Do With Your Cards", to: "/library/what-not-to-do-with-your-cards" },
      { label: "The Card Care Lab", to: "/card-care-lab" },
    ],
  },

  "before-you-grade-your-cards": {
    slug: "before-you-grade-your-cards",
    path: "/library/before-you-grade-your-cards",
    title: "Before You Grade Your Cards",
    metaDescription:
      "How to inspect surface, edges, corners, centering and overall presentation before submitting a card for grading — educational guidance from the CardScience HQ Card Care Lab.",
    excerpt:
      "Grading is an investment of time and money. Here's how to inspect a card honestly across the factors graders look at — so you submit with clear eyes.",
    datePublished: "2026-03-08",
    readTime: "6 min read",
    category: "Card Care Lab",
    hub: "card-care-lab",
    body: [
      { type: "p", text: "Before a card ever goes into a grading submission, it's worth looking at it the way a grader will. This isn't about predicting a number, it's about understanding your card honestly so the decision to grade is an informed one." },
      { type: "h2", text: "Inspect the surface" },
      { type: "p", text: "Under good, angled light, look for scratches, print lines, gloss breaks, fingerprints and haze. Tilt the card so light rakes across it, surface issues hide when light hits straight on." },
      { type: "h2", text: "Check the corners and edges" },
      { type: "p", text: "Look closely at all four corners for softness, fraying or whitening, and run your eye along each edge for nicks and chipping. Corners and edges are where wear shows first." },
      { type: "h2", text: "Assess centering" },
      { type: "p", text: "Compare the borders top-to-bottom and left-to-right, on both the front and the back. Centering is factory-original and cannot be changed responsibly, but knowing where your card stands helps set expectations." },
      { type: "impact", text: "Look at your card honestly \u2014 the grader will." },
      { type: "h2", text: "Judge overall presentation" },
      { type: "p", text: "Step back and take in the whole card: eye appeal, cleanliness and how the issues add up together. Sometimes a card with one flaw still presents beautifully; sometimes several small issues combine into a weaker overall look." },
      { type: "list", items: [
        "Surface \u2014 scratches, print lines, gloss, fingerprints",
        "Corners \u2014 sharpness vs. softness or whitening",
        "Edges \u2014 nicks, chipping, wear",
        "Centering \u2014 front and back borders",
        "Overall eye appeal and cleanliness",
      ]},
      { type: "h2", text: "Then decide \u2014 or ask" },
      { type: "p", text: "If you're unsure whether a card is worth submitting, that's exactly the question our pre-grading evaluation answers. We'll give you an honest read rather than encourage an unnecessary submission." },
      { type: "disclaimer", text: "This is educational guidance only. CardScience HQ is not a grading company and does not assign, predict or guarantee any grade. Independent grading companies determine all grades under their own standards." },
    ],
    related: [
      { label: "Should You Clean a Card Before Grading?", to: "/library/should-you-clean-a-card-before-grading" },
      { label: "The Card Care Lab", to: "/card-care-lab" },
    ],
  },

  "caring-for-cards-after-service": {
    slug: "caring-for-cards-after-service",
    path: "/library/caring-for-cards-after-service",
    title: "Caring for Your Cards After CardScience HQ",
    metaDescription:
      "How to handle, protect and store a card after professional service so it stays in the best possible condition \u2014 aftercare guidance from the CardScience HQ Card Care Lab.",
    excerpt:
      "Great service is only half the job. Here's how to handle and protect your card once it's back in your hands so the results last.",
    datePublished: "2026-03-09",
    readTime: "4 min read",
    category: "Card Care Lab",
    hub: "card-care-lab",
    body: [
      { type: "p", text: "When a card comes back from the lab, it's in the best condition we could responsibly achieve. What happens next is up to you, and a few simple habits keep it that way." },
      { type: "h2", text: "Handle it like it matters" },
      { type: "p", text: "Clean, dry hands. Hold by the edges. Keep it away from food, drinks and lotions. The same oils and dust that caused issues before will happily return if given the chance." },
      { type: "h2", text: "Protect it right away" },
      { type: "p", text: "Get the card back into appropriate protection promptly, a fresh sleeve and a rigid holder, or the holder we returned it in. Don't leave a freshly serviced card loose on a desk or table." },
      { type: "impact", text: "The best time to protect a card is the moment it's back in your hands." },
      { type: "h2", text: "Store it well, for the long run" },
      { type: "p", text: "Everything from our storage guide applies: cool, dry, dark and stable, upright rather than stacked, and away from direct light. Good aftercare is what turns a great result into a lasting one." },
      { type: "list", items: [
        "Handle by the edges with clean, dry hands",
        "Sleeve and protect immediately",
        "Store upright, cool, dry and out of light",
        "Avoid re-introducing oils, dust and moisture",
      ]},
      { type: "h2", text: "If you plan to grade or ship" },
      { type: "p", text: "If the card is heading to a grading company or a buyer, follow safe packaging and shipping practices, and wait for any required approval before mailing. A protected card that travels well arrives the way it left." },
      { type: "disclaimer", text: "Aftercare preserves condition; it does not alter it or guarantee any future grade. Results vary by card." },
    ],
    related: [
      { label: "How to Protect Cards When You Mail Them", to: "/library/protecting-cards-in-the-mail" },
      { label: "The Card Care Lab", to: "/card-care-lab" },
    ],
  },
};

// Ordered topic list for the /card-care-lab hub (maps to Library articles above).
export const CARE_LAB_TOPICS = [
  { n: "01", iconKey: "archive", slug: "how-to-store-your-cards", title: "How to Store Your Cards", copy: "Sleeves, rigid holders, storage orientation, temperature, humidity, light and long-term protection." },
  { n: "02", iconKey: "ban", slug: "what-not-to-do-with-your-cards", title: "What Not to Do", copy: "Common mistakes that can damage card surfaces, finishes, edges and corners." },
  { n: "03", iconKey: "package", slug: "card-care-products-we-trust", title: "Products We Use & Trust", copy: "Card-care and storage products selected for specific purposes \u2014 consumer protection, not proprietary process." },
  { n: "04", iconKey: "alert", slug: "why-cards-get-damaged", title: "Why Cards Get Damaged", copy: "Fingerprints, oils, dust, moisture, humidity, UV exposure, friction and pressure." },
  { n: "05", iconKey: "scan", slug: "before-you-grade-your-cards", title: "Before You Grade", copy: "Inspecting surface, edges, corners, centering and overall presentation before submission." },
  { n: "06", iconKey: "shield", slug: "caring-for-cards-after-service", title: "After CardScience HQ", copy: "How to handle and protect a card after professional service so the results last." },
];

export const ARTICLE_LIST = Object.values(ARTICLES);
